// Decision tree — ported from umag.html. A collapsible question/option tree;
// option nodes can carry a doc anchor that smooth-scrolls to the matching
// section in the article and briefly flashes it.
import { useState } from 'react';

interface TreeNodeData {
  q?: string;
  opt?: string;
  note?: string;
  tip?: string;
  doc?: string;
  recommended?: boolean;
  children?: TreeNodeData[];
}

const TREE: TreeNodeData[] = [
  {
    q: 'Legyenek-e fix párok a torna teljes idejére?',
    children: [
      {
        opt: 'Igen, fix párok',
        doc: 'opt-A1',
        children: [
          {
            q: 'Hogyan álljanak össze a párok?',
            children: [
              { opt: 'Areiosz Pagosz (a vének tanácsa)', doc: 'opt-B1' },
              {
                opt: 'Sorsolás',
                doc: 'opt-B2',
                children: [
                  {
                    q: 'Egy vagy két kalapból?',
                    children: [
                      { opt: 'Egy kalap', doc: 'opt-B2a' },
                      {
                        opt: 'Két kalap',
                        doc: 'opt-B2b',
                        children: [
                          {
                            q: 'A kalapok beosztása mi alapján?',
                            children: [
                              { opt: 'Önbevallás', tip: 'Mindenki bemondja, melyik kalapba tartozik.' },
                              { opt: 'Önkényes (vének tanácsa)', tip: 'A szervezők döntik el a beosztást.' },
                              { opt: 'Közösségi szavazás', tip: 'A résztvevők szavazata alapján.' },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                opt: 'Adat alapú párosítás',
                doc: 'opt-B3',
                children: [
                  {
                    q: 'Mérési módszer?',
                    children: [
                      { opt: 'Közösségi sorrendállítás', doc: 'opt-B3a' },
                      { opt: 'Élő pontrendszer', doc: 'opt-B3b' },
                      { opt: 'Képlet + képesség pontozás (1–5)', doc: 'opt-B3c' },
                      { opt: 'Képlet + képesség Élő pontok', doc: 'opt-B3d', recommended: true },
                    ],
                  },
                ],
              },
              {
                opt: 'Gale-Shapley / Irving algoritmus',
                doc: 'opt-B4',
                children: [
                  {
                    q: 'Egy vagy két kalapból?',
                    children: [
                      { opt: 'Egy kalap (mindenki vs. mindenki)', tip: 'A teljes mezőnyre egy preferencia-sorrend.' },
                      {
                        opt: 'Két kalap (csak kalapon belül)',
                        tip: 'A preferencia-sorrend csak a saját kalap tagjaira vonatkozik.',
                        children: [
                          {
                            q: 'A kalapok beosztása mi alapján?',
                            children: [
                              { opt: 'Önbevallás' },
                              { opt: 'Önkényes (vének tanácsa)' },
                              { opt: 'Közösségi szavazás' },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            q: 'Milyen legyen a verseny lebonyolítása?',
            children: [
              {
                opt: 'Két csoport, csoportmeccsek + elődöntő + döntő',
                doc: 'opt-C1',
                children: [
                  {
                    q: 'A csoportok beosztása?',
                    children: [
                      { opt: 'Kiegyensúlyozott csoportok', doc: 'opt-C1b' },
                      {
                        opt: 'Erősebb és gyengébb csoport',
                        doc: 'opt-C1a',
                        children: [
                          {
                            q: 'Hogyan dőljön el a végső győztes?',
                            children: [
                              { opt: 'Csoportonként külön győztes' },
                              { opt: 'Kereszt elődöntő + közös döntő' },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              { opt: 'Egy nagy csoport (mindenki–mindenkivel)', doc: 'opt-C2' },
              {
                opt: 'Svájci rendszer',
                doc: 'opt-C3',
                children: [
                  {
                    q: 'Legyen-e döntő szakasz?',
                    children: [
                      { opt: 'Csak svájci körök, az állás dönt' },
                      { opt: 'Svájci körök + elődöntő + döntő' },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        opt: 'Nem, cserélődő párok',
        doc: 'opt-A2',
        children: [
          {
            q: 'Milyen forgási rendszer?',
            children: [
              { opt: 'Americano (fix forgási sorrend)', doc: 'opt-C4' },
              { opt: 'Mexikói (svájci alapú forgás)', doc: 'opt-C5' },
            ],
          },
          {
            q: 'Az első kör párosításai mi alapján?',
            children: [
              { opt: 'Véletlen' },
              { opt: 'Önbevallás alapján' },
              { opt: 'Korábbi évek értékelései alapján (ha van)' },
            ],
          },
        ],
      },
    ],
  },
];

// Nodes at depth <= INITIAL_DEPTH start expanded.
const INITIAL_DEPTH = 2;

function labelFromAnchor(id: string) {
  return id.replace(/^opt-/, '');
}

function scrollToAnchor(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const targetBlock = el.closest('.option, .digression, h4') || el;
  targetBlock.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const flash = (node: Element) => {
    node.classList.remove('scroll-target-flash');
    void (node as HTMLElement).offsetWidth; // force reflow so it can replay
    node.classList.add('scroll-target-flash');
  };
  flash(targetBlock);
  flash(el);
}

function TreeNode({ node, depth }: { node: TreeNodeData; depth: number }) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const startOpen = hasChildren && depth < INITIAL_DEPTH;
  const [open, setOpen] = useState(startOpen);
  // Unread dot latches off once the node has ever been opened.
  const [everOpened, setEverOpened] = useState(startOpen);

  const toggle = () => {
    if (!hasChildren) return;
    setOpen((o) => {
      if (!o) setEverOpened(true);
      return !o;
    });
  };

  const hasUnread = hasChildren && !everOpened;

  return (
    <li className="tree-node">
      <div
        className={`tree-row${hasUnread ? ' has-unread' : ''}`}
        style={hasChildren ? { cursor: 'pointer' } : undefined}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('.tree-doclink')) return;
          toggle();
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className={`tree-chevron${open ? ' is-open' : ''}`}
            aria-label="Kibontás / összecsukás"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        ) : (
          <span className="tree-spacer" aria-hidden="true" />
        )}

        <div className="tree-label">
          {node.q ? (
            <span className="tree-question">{node.q}</span>
          ) : (
            <>
              <span className="tree-option">
                {node.opt}
                {node.recommended && <span className="tree-recommended">— ajánlott</span>}
              </span>
              {node.tip && <span className="tree-note">{node.tip}</span>}
            </>
          )}
        </div>

        {node.doc && (
          <a
            className="tree-doclink"
            href={`#${node.doc}`}
            aria-label={`Ugrás a(z) ${labelFromAnchor(node.doc)} szekcióra`}
            onClick={(e) => {
              e.preventDefault();
              scrollToAnchor(node.doc!);
            }}
          >
            {labelFromAnchor(node.doc)}
          </a>
        )}
      </div>

      {hasChildren && (
        <ul className={`tree-children${open ? ' is-open' : ''}`}>
          {node.children!.map((child, i) => (
            <TreeNode key={i} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function CikkDecisionTree() {
  return (
    <div className="tree-frame">
      <div className="decision-tree" aria-label="Döntési fa">
        <ul
          className="tree-children is-open"
          style={{ borderLeft: 'none', paddingLeft: 0, marginLeft: 0 }}
        >
          {TREE.map((node, i) => (
            <TreeNode key={i} node={node} depth={0} />
          ))}
        </ul>
      </div>
    </div>
  );
}
